// Modern Google Maps implementation using @googlemaps/js-api-loader
import { importLibrary } from "./google_maps_loader"

class CrawlMap {
  constructor() {
    this.map = null
    this.markers = []
    this.directionsRenderers = []
    this.routeVisible = true
    this.stops = []
    this.apiKey = null
  }

  async initialize(apiKey, stops, currentStop, completedStops) {
    try {
      console.log("[Maps] Initializing CrawlMap instance")

      // Store API key for reference
      this.apiKey = apiKey

      // Import required libraries - API key should already be set from module level
      console.log("[Maps] Importing maps library")
      const mapsLib = await importLibrary("maps")
      console.log("[Maps] Importing marker library")
      const markerLib = await importLibrary("marker")

      // Store stops data
      this.stops = stops

      // Initialize the map
      await this.initMap(mapsLib, markerLib, currentStop, completedStops)
    } catch (error) {
      console.error("Error loading Google Maps:", error)
    }
  }

  async initMap(mapsLib, markerLib, currentStop, completedStops) {
    const defaultPosition = { lat: 40.7128, lng: -74.006 }

    // Import routing library for directions
    console.log("[Maps] Importing routes library for directions")
    const routesLib = await importLibrary("routes")
    console.log("[Maps] Importing core library")
    const coreLib = await importLibrary("core")
    console.log("[Maps] All libraries imported successfully")

    // Store event for later use
    this.googleMapsEvent = coreLib.event

    this.map = new mapsLib.Map(document.getElementById("map"), {
      zoom: 13,
      center: defaultPosition,
      styles: [
        { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
        { featureType: "administrative.neighborhood", stylers: [{ visibility: "off" }] },
        { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
        { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
        { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
        { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
        { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
        {
          featureType: "road.highway",
          elementType: "geometry.stroke",
          stylers: [{ color: "#1f2835" }],
        },
        {
          featureType: "road.highway",
          elementType: "labels.text.fill",
          stylers: [{ color: "#f3d19c" }],
        },
        { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
        { featureType: "water", elementType: "labels.text", stylers: [{ visibility: "off" }] },
      ],
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: true,
    })

    const bounds = new coreLib.LatLngBounds()

    if (this.stops.length > 0) {
      this.stops.forEach((stop, i) => {
        const marker = new markerLib.Marker({
          position: stop.position,
          map: this.map,
          title: stop.title,
          label: {
            text: stop.label,
            color: "white",
            fontSize: "11px",
            fontWeight: "bold",
          },
          icon: {
            path: coreLib.SymbolPath.CIRCLE,
            fillColor: stop.current ? "#f3c023" : stop.completed ? "#a0a0a0" : "#f3c023",
            fillOpacity: 1,
            strokeColor: "#FFFFFF",
            strokeWeight: 2,
            scale: 12,
          },
          zIndex: stop.current ? 100 : i + 1,
        })

        const infoWindow = new mapsLib.InfoWindow({
          content: `
            <div style="padding: 8px; max-width: 200px; font-family: helvetica-lt-pro, sans-serif; font-weight: 700;">
              <div style="font-size: 16px; font-weight: bold; margin-bottom: 4px; color: #f3c023;">
                ${stop.current ? "● " : ""}${stop.title}
              </div>
              <div style="font-size: 14px; color: #475569; margin-bottom: 4px;">
                Stop #${stop.label}
                ${stop.current ? ' <span style="color: #f3c023; font-weight: bold;">(Current)</span>' : ""}
                ${stop.completed ? ' <span style="color: #64748b;">(Completed)</span>' : ""}
              </div>
              <div style="margin-top: 8px;">
                <a href="javascript:void(0)"
                   onclick="window.crawlMap.scrollToStop('${stop.current ? "current_stop_" : ""}stop_${stop.label}')"
                   style="color: #f3c023; text-decoration: underline; font-size: 12px;">
                   View in schedule
                </a>
              </div>
            </div>
          `,
          maxWidth: 250,
          pixelOffset: new coreLib.Size(0, -5),
        })

        marker.addListener("click", () => {
          this.markers.forEach((m) => {
            if (m.infoWindow && m.infoWindow.close) {
              m.infoWindow.close()
            }
          })
          infoWindow.open({ anchor: marker, map: this.map })
          marker.infoWindow = infoWindow
        })

        this.markers.push(marker)
        bounds.extend(stop.position)
      })

      if (this.stops.length > 1) {
        this.createWalkingDirections(routesLib, mapsLib)
      }

      // Fit map bounds to show all stops with padding
      const padding = { top: 50, right: 50, bottom: 50, left: 50 }
      this.map.fitBounds(bounds, padding)

      // For single stop, zoom in more after fitBounds
      if (this.stops.length === 1) {
        this.map.setZoom(15)
      }

      this.setupMapControls(coreLib)
      this.setupStopRowListeners()
    }
  }

  createWalkingDirections(routesLib, mapsLib) {
    const directionsService = new routesLib.DirectionsService()
    this.directionsRenderers.forEach((renderer) => renderer.setMap(null))
    this.directionsRenderers = []

    for (let i = 0; i < this.stops.length - 1; i++) {
      const start = this.stops[i].position
      const end = this.stops[i + 1].position

      const renderer = new routesLib.DirectionsRenderer({
        map: this.map,
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: "#f3c023",
          strokeOpacity: 1.0,
          strokeWeight: 4,
        },
      })

      this.directionsRenderers.push(renderer)

      const request = {
        origin: start,
        destination: end,
        travelMode: "WALKING",
      }

      directionsService.route(request, (result, status) => {
        if (status === "OK") {
          renderer.setDirections(result)
        } else {
          console.error("Directions request failed due to " + status)
          const fallbackPath = new mapsLib.Polyline({
            path: [start, end],
            geodesic: true,
            strokeColor: "#f3c023",
            strokeOpacity: 0.7,
            strokeWeight: 3,
            icons: [
              {
                icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 2 },
                offset: "0",
                repeat: "20px",
              },
            ],
          })
          fallbackPath.setMap(this.map)
          this.directionsRenderers.push({
            setMap: function (m) {
              fallbackPath.setMap(m)
            },
            isFallback: true,
          })
        }
      })
    }
  }

  toggleRouteVisibility(visible) {
    this.directionsRenderers.forEach((renderer) => {
      renderer.setMap(visible ? this.map : null)
    })
    this.routeVisible = visible
  }

  scrollToStop(elementId) {
    const stopElement = document.getElementById(elementId)
    if (stopElement) {
      const completedSection = document.getElementById("completed-stops")
      if (
        completedSection &&
        completedSection.classList.contains("hidden") &&
        elementId.startsWith("stop_") &&
        !elementId.startsWith("current_stop_")
      ) {
        const toggleBtn = document.getElementById("toggle-completed")
        if (toggleBtn) toggleBtn.click()
        setTimeout(() => {
          stopElement.scrollIntoView({ behavior: "smooth", block: "center" })
          stopElement.classList.add("highlight-stop")
          setTimeout(() => stopElement.classList.remove("highlight-stop"), 2000)
        }, 500)
      } else {
        stopElement.scrollIntoView({ behavior: "smooth", block: "center" })
        stopElement.classList.add("highlight-stop")
        setTimeout(() => stopElement.classList.remove("highlight-stop"), 2000)
      }
    }
  }

  setupMapControls(coreLib) {
    const resetButton = document.getElementById("reset-map-view")
    if (resetButton) {
      resetButton.addEventListener("click", () => {
        if (this.map && this.markers.length > 0) {
          const bounds = new coreLib.LatLngBounds()
          this.markers.forEach((marker) => bounds.extend(marker.getPosition()))
          this.map.fitBounds(bounds)
        }
      })
    }

    const toggleLineButton = document.getElementById("toggle-route-line")
    if (toggleLineButton) {
      toggleLineButton.addEventListener("click", () => {
        this.routeVisible = !this.routeVisible
        this.toggleRouteVisibility(this.routeVisible)
        toggleLineButton.querySelector("span").textContent = this.routeVisible
          ? "Hide Route Line"
          : "Show Route Line"
      })
    }
  }

  setupStopRowListeners() {
    const stopRows = document.querySelectorAll(".public-crawl-stop-row")
    stopRows.forEach((stopRow) => {
      stopRow.addEventListener("click", () => {
        const stopId = stopRow.id
        if (stopId) {
          const position = stopId.split("_").pop()
          const markerIndex = this.stops.findIndex((stop) => stop.label === position)
          if (markerIndex >= 0 && this.markers[markerIndex]) {
            this.googleMapsEvent.trigger(this.markers[markerIndex], "click")
            this.map.panTo(this.markers[markerIndex].getPosition())
            this.map.setZoom(15)
          }
        }
      })
    })
  }
}

// Export the class and make it globally available
export default CrawlMap

// Initialize on page load
function initializeCrawlMap() {
  const mapElement = document.getElementById("map")
  if (!mapElement) return

  const crawlDataElement = document.getElementById("crawl-data")
  if (!crawlDataElement) return

  // Get API key from meta tag
  const apiKeyMeta = document.querySelector('meta[name="google-maps-api-key"]')
  if (!apiKeyMeta) {
    console.error("[Maps] Google Maps API key meta tag not found")
    return
  }

  const apiKey = apiKeyMeta.getAttribute("content")
  if (!apiKey) {
    console.error("[Maps] Google Maps API key not found")
    return
  }

  // Get stops data from data attributes
  const stopsData = mapElement.dataset.stops
  if (!stopsData) {
    console.error("[Maps] Stops data not found")
    return
  }

  const stops = JSON.parse(stopsData)
  const currentStop = mapElement.dataset.currentStop
  const completedStops = mapElement.dataset.completedStops
    ? JSON.parse(mapElement.dataset.completedStops)
    : []

  // Create and initialize the map
  const crawlMap = new CrawlMap()
  window.crawlMap = crawlMap // Make it globally accessible for inline event handlers
  crawlMap.initialize(apiKey, stops, currentStop, completedStops)
}

// Listen for Turbo events
document.addEventListener("turbo:load", initializeCrawlMap)
document.addEventListener("DOMContentLoaded", initializeCrawlMap)
